/**********************************************************
 * Encapsulates the data and actions associated with
 * product swapping feature.
 *
 * On the implementation side, a swap really consists of:
 *  (1) Replacment phase
 *  (2) Render(s) in progress phase(s)
 *
 * Replacements apply at the Design level and result in a
 * new design revision if the replacment is successful. We
 * limit the number of active replacements per Design to
 * one at this time but replacements are only expected to
 * take a few seconds.
 *
 * After a successful replacment, every render in a
 * revision can be thought of as a render that could be
 * avaiable sometime in the future. The actual render is
 * kicked off by an API request, then at some point in the
 * future the render will have status finished and will be
 * avaialable to display.
 *
 * IMPORTANT:
 * This means that if we (1) perform consecutive replacements
 * from the same camera angle, then (2) change camera angles,
 * the resuling render in progress we are polling covers 2
 * swaps/replacments.
 *
 * IMPORTANT:
 * Its also the case that we can have multiple renders in
 * progress at one time. I.e., if user initiates a swap and
 * then cycles through the camera angles, we should have
 * 1 render in progress for each camera angle.
 *
 * IMPORANT:
 * The render(s) in progress phase can really occur at any
 * time even if there have been zero swaps in a session.
 * Consider on reload after a successful replacement there
 * will likely be render futures that have yet to be
 * viewed/requested. Attempting to view such renders should
 * kick off a render in progress phase(s).
 *
 * IMPORTANT:
 * Camera name strings are used as IDs in backend API
 * responses. In order to match up cameras across cloned
 * designs and revisions within a design, we need to make
 * sure we are not camelcasing the camera name/id strings.
 * Also important when/if we need to include the camera
 * name in requests back to server.
 **********************************************************/
import get from 'lodash/get';
import unique from 'lodash/uniq';
import { createSelector } from 'reselect';

import analytics from 'services/analytics';
import { bugsnagWarn, bugsnagError } from 'services/bugsnag';
import warehouse from 'services/warehouse';

import {
  addOrUpdateSpace,
  getActiveDesign,
  getDesignFor,
  getRenderFor,
  getSpaceFor,
  selectHotspotProduct,
  studioAddOrUpdateDesign,
  studioAddOrUpdateRenders,
  studioDeleteDesign,
  studioDesignLoaded,
  studioDesignsnapshotLoaded,
  studioUpdateProduct,
  getIndexedProducts,
  getSelectedHotspotProduct,
  getRenders
} from 'redux/Studio';

import {
  navigateToStudioSpaceAndDesignFor
} from 'services/routing';

import { Design, Render } from 'redux/models';

import {
  getActiveRender,
  studioSelectRender,
  setMobileHotspotsView,
  MOBILE_HOTSPOTS_VIEWS
} from 'redux/Studio/DesignViewer';

import {
  ALREADY_SWAPPING_ON_DESIGN,
  CURRENT_DESIGN_COMPLETE,
  CURRENT_DESIGN_FAILED,
  CURRENT_DESIGN_CANCELLED,
  PRODUCT_NOT_SWAPPABLE,
  LONGER_SWAP,
  NETWORK_ERROR
} from 'screens/Studio/Studio/components/SwapProducts/constants';

import {
  getCurrentRenderInProgress,
  getRendersInProgress,
  getRenderInProgress,
  getSwapData
} from 'redux/Studio/SwapProducts/selectors';

import {
  updateShowSwapNotification
} from 'redux/Studio/SwapProducts/actions';

import RenderInProgress from './RenderInProgress';
import Replacement from './Replacement';

/**
 * Manages product swap actions including
 * replacements and renders in progress.
 */
export default class SwapService {
  /**
    * Maximum attempted liveswap fetch render requests.
    * EG: 48 attempts with max time of 240 and interval of 5
    * @type {Number}
    */
  static MAX_TIME_IN_SECONDS = 240;
  static INTERVAL_IN_SECONDS = 5;
  static MAX_LIVESWAP_ATTEMPTS = SwapService.MAX_TIME_IN_SECONDS / SwapService.INTERVAL_IN_SECONDS;

  // Swap error categories
  static SWAP_ERROR_PRODUCT_NOT_SWAPPABLE = 'SwapErrorProductNotSwappable';
  static SWAP_ERROR_ACTIVE_REPLACEMENT = 'SwapErrorActiveReplacement';

  // ============================
  // ACTIONS / CALLBACK DATA
  // ============================
  /***
   * callback SwapService~actionsCallback
   * param {Object} action
   * param {string} action.type - The action type string
   * param {string} action.payload - The action data
   */

  /**
    * Action fired whenever replacement data is updated.
    * @event SwapService#updated
    * @type {Object}
    * @property {string} type
    * @property {Replacement} payload
    */
  static REPLACEMENT_DATA_UPDATED = 'SWAP_SERVICE/REPLACE_DATA_UPDATED';

  /**
    * Action fired whenever a render in progress is updated.
    * @event SwapService#ripUpdated
    * @type {Object}
    * @property {string} type
    * @property {RenderInProgress} payload
    */
  static RENDER_IN_PROGRESS_UPDATED = 'SWAP_SERVICE/RENDER_IN_PROGRESS_UPDATED';

  /**
    * Action fired when multiple renders in progress are updated.
    * @event SwapService#updateRendersInProgress
    * @type {Object}
    * @property {string} type
    * @property {Array[RenderInProgress]} payload
    */
  static RENDERS_IN_PROGRESS_UPDATED = 'SWAP_SERVICE/RENDERS_IN_PROGRESS_UPDATED';

  // =========================================================
  // ACTION CREATORS / MUTATORS
  //
  // IMPORTANT:
  // Any model mutations should follow immutable pattern:
  //  (1) do work off to the side
  //  (2) make a copy
  //  (3) notify callback with the copy
  // =========================================================

  /**
   * Main entry point for initiating a product swap.
   *
   * param {Object} swapDetails
   * @param {string} swapDetails.cameraId - The camera angle swap was initiated from.
   * @param {Design} swapDetails.design - The design instance swap was initated from.
   * @param {Array} swapDetails.hotspotUuids - An array of the hotspot UUIDs associated with product to be replaced in swap.
   * @param {Product} swapDetails.replaceProduct - The instance of Product to be replaced in swap.
   * @param {Product} swapDetails.swapInProduct - The instance of Product to be swapped in.
   * @param {Callback} dispatch - SwapService~actionsCallback
   *
   * @fires SwapService#updated
   */
  static swapProduct = (incomingSwap) => async (dispatch, getState) => {
    const {
      cameraId,
      design,
      hotspotUuids,
      replaceProduct,
      swapInProduct
    } = incomingSwap;

    const currentSpace = () => getSpaceFor({
      spaceId: design.parentSpaceId(),
      state: getState()
    });

    const currentReplacement = () => {
      return getSwapData(getState());
    };

    // need to define here for access in catch
    let whDesignSnapshot;
    let replacement;

    // We will wrap the entire action in a try/catch
    // with a catchall error status of product not swappable
    // for any errors not explicitly handled.
    try {
      // Handle case when there is already an in progress swap.
      // We currently only allow one in progress swap.
      // Note: this restriction does not apply to renders-in-progress.
      if (currentReplacement().isInProgress()) {
        const error = 'There is already a swap in progress';
        bugsnagWarn(error, {
          incomingSwap,
          currentSwap: currentReplacement().fields
        });

        dispatch(updateShowSwapNotification({
          status: true,
          design,
          messageType: ALREADY_SWAPPING_ON_DESIGN
        }));

        return;
      }

      // Construct the replacement instance.
      replacement = new Replacement({
        cameraId,
        designId: design.id(),
        revisionUuid: design.revisionUuid(),
        hotspotUuids,
        replaceProductId: replaceProduct.id(),
        swapInProductId: swapInProduct.id(),
        status: 'InProgress'
      });
      console.log('Replacement Data', replacement.fields); // intentionally leaving in

      // Add the replacement instance to state.
      // This will allow controllers and their
      // associated views to update right away
      // to reflect active replacement is in progress.
      dispatch(SwapService._updateReplacementState(replacement));

      // Handle non-swappable hotspot uuids.
      if (hotspotUuids.some(uuid => uuid.length < 20)) {
        throw new Error('Invalid hotspot UUID');
      }

      // Add the swapInProduct instance to state.
      dispatch(studioUpdateProduct(swapInProduct));

      // Make request to BE to do the replacement.
      // The expected result is a designsnapshot
      // representing a design revision. A designsnapshot
      // can be in one of the following states:
      //   queued | preparing | failed | ready.
      // The return state from the intial API call will be
      // either queued or failed.
      //
      // TODO: add a BE API for polling a designsnapshot
      // so we can key off ready or failed states to signal
      // the end of replacement phase.
      //
      // For now, our best approximation is to poll the
      // render. A status of 202 indicates that the replace
      // was successful (and also has the benefit of initiating
      // the render-in-progress). A status of 409 indicates the
      // replace is either in preparing or failed state. We
      // can inspect the error detail for 'failed' to indicate a
      // replace error otherwise assume prepare is still in progress.
      //
      // If we do not get a 202 status within 30 seconds we should
      // just assume that the replace has failed.
      whDesignSnapshot = await warehouse.swapProduct({
        designId: replacement.designId(),
        hotspotInstanceUids: replacement.hotspotUuids(),
        oldProductId: replacement.replaceProductId(),
        newProductId: replacement.swapInProductId(),
        snapshotId: replacement.revisionUuid()
      });
      console.log('Designsnapshot', whDesignSnapshot); // intentionally leaving in

      if (whDesignSnapshot.status === 'failed') {
        throw new Error(`Live swap prepare failed for designsnapshot: ${whDesignSnapshot.id}`);
      }

      // Check for and handle cancelations.
      const swapHasBeenCanceled = () => {
        return currentReplacement().hasBeenCanceled();
      };

      const handleCanceledSwap = () => {
        dispatch(SwapService._handleCancelSwap({ incomingSwap, whDesignSnapshot }));
      };

      if (swapHasBeenCanceled()) { return handleCanceledSwap(); }

      // Determine the render uuid in the revision that corresponds to
      // the render swap was initiated from.
      const renderUuid = get(whDesignSnapshot, `renderings[${cameraId}].live_swap`, null);
      if (!renderUuid) {
        throw new Error(`Cannot find camera ${cameraId} in designsnapshot ${whDesignSnapshot.id}`);
      }

      // Request the full design with swap/revison from BE.
      // This is so internal revision and render objects we
      // create will come from the exact same API call regardless
      // of whether we are doing a live swap or first load of a
      // design with revisions. Also, if the swap was initated from
      // a Modsy design, then this will return the new Playground
      // design.
      //
      // Note: for swaps initated from a Modsy design, we should NOT
      // show the modal allowing users to switch over to the Playground
      // until this API response returns. Granted, if Product requirements
      // are inflexible and require the modal and switch over are presented
      // at a fixed time, then we can always create a cloned fake design and
      // just overwrite it with the real one returned in this response.
      const updatedDesignBrief = Design.createDesignFromWarehouseResponse({ spaceId: design.parentSpaceId(), whDesign: await warehouse.getDesignBrief(whDesignSnapshot.design_pk), showroomSpace: currentSpace().isShowroomSpace() });
      const updatedDesign = await warehouse.getDesign(whDesignSnapshot.design_pk);
      console.log('Design With Snapshot', updatedDesign); // intentionally leaving in

      const designsnapshot = updatedDesign.designsnapshot;
      if (designsnapshot.status === 'failed') {
        throw new Error(`Live swap prepare failed for designsnapshot: ${designsnapshot.id}`);
      }

      if (swapHasBeenCanceled()) { return handleCanceledSwap(); }

      // Update state with the design, revision, and renders.
      //
      // For swaps initiated from a Modsy design, we will be adding
      // the new playground design to state in addition to revision and
      // renders from the swap. We are assuming user will then switch over
      // to the new playground design. If the user cancels, then we need to
      // remove the new playground design from state.
      //
      // For swaps initiated from a playground design, we need to add the
      // revision and renders from the swap plus update the existing
      // playground design with the new revision uuid and swappedInProduct.
      const isNewDesign = updatedDesign.id !== design.id();

      let resultingDesign;
      if (isNewDesign) {
        dispatch(analytics.trackEvent({
          event: 'New Playground Design Created',
          data: {
            design_id: updatedDesign.id,
            space_id: design.parentSpaceId()
          }
        }));

        dispatch(studioDesignLoaded({
          spaceId: design.parentSpaceId(),
          whDesign: updatedDesign,
          showroomSpace: currentSpace().isShowroomSpace()
        }));

        resultingDesign = getDesignFor({
          designId: updatedDesign.id,
          state: getState()
        });

        // set author info from the new brief
        resultingDesign.setAuthorInfo(updatedDesignBrief.createdAt(), updatedDesignBrief.createdBy());

        // Update the space with new design.
        const updatedSpace = currentSpace().makeCopyWithNewDesign(resultingDesign);
        dispatch(addOrUpdateSpace(updatedSpace));

        // Update the replacement with the new design ID and revision UUID.
        dispatch(SwapService._updateReplacementState(
          currentReplacement().makeCopy({
            designId: updatedDesign.id,
            revisionUuid: designsnapshot.id
          })
        ));
      } else {
        dispatch(studioDesignsnapshotLoaded({ whDesignSnapshot: designsnapshot }));

        // Update the replacement with the new revision UUID.
        dispatch(SwapService._updateReplacementState(
          currentReplacement().makeCopy({
            revisionUuid: designsnapshot.id
          })
        ));

        // Update playground design with the new revision and products.
        resultingDesign = design.makeCopyWithRevision({
          revisionUuid: designsnapshot.id,
          addlProducts: [swapInProduct.id()],
          removeProducts: [replaceProduct.id()]
        });
        dispatch(studioAddOrUpdateDesign(resultingDesign));
      }

      // Poll on the revision render for camera swap was initiated on.
      // When we get a finished status we know that the replacement
      // was successful. Here, we will do up to 24 polls at an inteveral
      // of 5 seconds before timing out and assuming the swap failed.
      let render = null;
      let updatedDesignSnapshot = false;
      for (let attempt = 1; attempt <= 48; attempt++) {
        if (swapHasBeenCanceled()) { return handleCanceledSwap(); }

        render = await warehouse.getRender({ renderUuid, attempt });
        console.log(`Fetch Swap Render ${renderUuid} - Attempt ${attempt}`, render); // intentionally leaving in

        // when the render progresses past the preparing phase for the first time
        // get the design snapshot again
        // this updates the newInstanceIds for revisions
        if (render.status !== 'preparing' && render.status !== 'failed' && !updatedDesignSnapshot) {
          updatedDesignSnapshot = true;
          let designSnapshot = (await warehouse.getDesign(whDesignSnapshot.design_pk)).designsnapshot;
          if (designSnapshot) { dispatch(studioDesignsnapshotLoaded({ whDesignSnapshot: designSnapshot })); }
        }

        if (render.status === 'finished') {
          break;
        }

        if (render.status === 'failed') {
          throw new Error('fetchRender failed: ' + renderUuid);
        }

        if (swapHasBeenCanceled()) { return handleCanceledSwap(); }

        if (attempt === 24) {
          // Show the 'taking longer' notification
          dispatch(updateShowSwapNotification({
            cameraId,
            design: resultingDesign,
            status: true,
            productIds: [swapInProduct.id()],
            messageType: LONGER_SWAP
          }));
        }

        await wait5seconds();
      }

      if (swapHasBeenCanceled()) { return handleCanceledSwap(); }

      if (render.status === 'failed') {
        throw new Error('fetchRender failed: ' + renderUuid);
      }

      // throw special error for prepare timing out
      if (render.status === 'preparing') {
        throw new Error(`Live swap prepare timed out for designsnapshot: ${designsnapshot.id}`);
      }

      if (render.status !== 'finished') {
        throw new Error('Live swap render timed out');
      }

      // ========
      // SUCCESS
      // ========
      // Update Render instance in state to trigger displaying the new render.
      const renderUid = Render.createRenderUid({ revisionUuid: designsnapshot.id, cameraName: cameraId });
      const renderV2 = getRenderFor({ renderUid, state: getState() });
      const updatedRenderV2 = renderV2.makeCopyWithUpdatedDraftVariant(render);
      dispatch(studioAddOrUpdateRenders([updatedRenderV2]));

      // Update design brief updateAt time.
      const updatedSpace = currentSpace().makeCopyUpdateDesignBriefUpdatedAt(
        updatedDesign.id, designsnapshot.updated_at
      );
      dispatch(addOrUpdateSpace(updatedSpace));

      // Show the swap success notification
      dispatch(updateShowSwapNotification({
        cameraId,
        design: resultingDesign,
        status: true,
        renderUid,
        productIds: [swapInProduct.id()],
        messageType: CURRENT_DESIGN_COMPLETE
      }));

      // the replacement phase has succeeded.
      // We can set the status to completed.
      const replacementComplete = currentReplacement().makeCopy({ status: 'Completed' });
      dispatch(SwapService._updateReplacementState(replacementComplete));

      // If we are on the same design as the swap and the currently
      // selected product is the replaced product, then select the
      // swapped in product.
      const activeDesign = getActiveDesign({ state: getState() });
      const selectedProduct = getSelectedHotspotProduct(getState());
      if (
        activeDesign &&
        activeDesign.id() === currentReplacement().designId() &&
        selectedProduct &&
        selectedProduct.id() === currentReplacement().replaceProductId()
      ) {
      // Find the swapInProduct in the new render and select it.
      // If we cannot find a hotspot matching the product in the
      // new render, then fallback to unselecting the hotspot.
        const newHotspot = render.hotspots.find(hotspot => hotspot.product_id === swapInProduct.id());
        const hotspotUid = newHotspot ? newHotspot.instance_id : null;
        const productId = hotspotUid ? swapInProduct.id() : null;
        dispatch(selectHotspotProduct(productId, hotspotUid));
      }

      dispatch(analytics.trackEvent({
        event: 'Studio: Swap Success',
        data: {
          design_id: design.id(),
          space_id: design.parentSpaceId(),
          replaced_product_id: replaceProduct.id(),
          swapIn_product_id: swapInProduct.id()
        }
      }));
    } catch (error) {
      dispatch(SwapService._handleCancelSwap({ incomingSwap, whDesignSnapshot }));

      if (get(error, 'message') === 'Invalid hotspot UUID') {
        dispatch(updateShowSwapNotification({
          status: true,
          design,
          productIds: [replacement.swapInProductId()],
          messageType: PRODUCT_NOT_SWAPPABLE
        }));
      } else if (error.toString().includes('Network Error')) {
        dispatch(updateShowSwapNotification({
          status: true,
          design,
          productIds: [replacement.swapInProductId()],
          messageType: NETWORK_ERROR
        }));
      } else {
        dispatch(updateShowSwapNotification({
          status: true,
          design,
          productIds: [replacement.swapInProductId()],
          messageType: CURRENT_DESIGN_FAILED
        }));
      }

      if (get(error, 'message').includes('fetchRender failed')) {
        // update render variant in revision as 'failed'
        // otherwise they stay as unfinished even though we got an error

        // extract renderUuid
        let message = get(error, 'message');
        let renderUuid = message.split(' ').pop();
        SwapService.updateRenderFailed(renderUuid, dispatch, getState());
      }

      dispatch(SwapService._handleSwapError(
        SwapService.SWAP_ERROR_PRODUCT_NOT_SWAPPABLE,
        undefined,
        incomingSwap,
        error,
        design
      ));
    }
  };

  /**
   * Batch entry point for initiating a product swap. Fetches all design renders
   * from latest design revision in batch vs. just the render swap was initiated from
   * param {Object} swapDetails
   * @param {string} swapDetails.cameraId - The camera angle swap was initiated from.
   * @param {Design} swapDetails.design - The design instance swap was initated from.
   * @param {Array} swapDetails.hotspotUuids - An array of the hotspot UUIDs associated with product to be replaced in swap.
   * @param {Product} swapDetails.replaceProduct - The instance of Product to be replaced in swap.
   * @param {Product} swapDetails.swapInProduct - The instance of Product to be swapped in.
   * @param {Callback} dispatch - SwapService~actionsCallback
   *
   * @fires SwapService#updated
   */
  static swapProductBatch = (incomingSwap) => async (dispatch, getState) => {
    const {
      cameraId,
      design,
      hotspotUuids,
      replaceProduct,
      swapInProduct,
      isFromHotspot = false,
      updatedMessengerSwapUIEnabled = false
    } = incomingSwap;

    const currentSpace = () => getSpaceFor({
      spaceId: design.parentSpaceId(),
      state: getState()
    });

    const currentReplacement = () => {
      return getSwapData(getState());
    };

    // need to define here for access in catch
    let whDesignSnapshot;
    let replacement;
    let rendersToFetch;

    // We will wrap the entire action in a try/catch
    // with a catchall error status of product not swappable
    // for any errors not explicitly handled.
    try {
      // Handle case when there is already an in progress swap.
      // We currently only allow one in progress swap.
      // Note: this restriction does not apply to renders-in-progress.
      if (currentReplacement().isInProgress()) {
        const error = 'There is already a swap in progress';
        bugsnagWarn(error, {
          incomingSwap,
          currentSwap: currentReplacement().fields
        });

        dispatch(updateShowSwapNotification({
          status: true,
          design,
          messageType: ALREADY_SWAPPING_ON_DESIGN
        }));

        return;
      }

      // Construct the replacement instance.
      replacement = new Replacement({
        cameraId,
        designId: design.id(),
        revisionUuid: design.revisionUuid(),
        hotspotUuids,
        replaceProductId: replaceProduct.id(),
        swapInProductId: swapInProduct.id(),
        status: 'InProgress'
      });
      console.log('Replacement Data', replacement.fields); // intentionally leaving in

      // Add the replacement instance to state.
      // This will allow controllers and their
      // associated views to update right away
      // to reflect active replacement is in progress.
      dispatch(SwapService._updateReplacementState(replacement));

      // Handle non-swappable hotspot uuids.
      if (hotspotUuids.some(uuid => uuid.length < 20)) {
        throw new Error('Invalid hotspot UUID');
      }

      // Add the swapInProduct instance to state.
      dispatch(studioUpdateProduct(swapInProduct));

      // Make request to BE to do the replacement.
      // The expected result is a designsnapshot
      // representing a design revision. A designsnapshot
      // can be in one of the following states:
      //   queued | preparing | failed | ready.
      // The return state from the intial API call will be
      // either queued or failed.
      //
      // TODO: add a BE API for polling a designsnapshot
      // so we can key off ready or failed states to signal
      // the end of replacement phase.
      //
      // For now, our best approximation is to poll the
      // render. A status of 202 indicates that the replace
      // was successful (and also has the benefit of initiating
      // the render-in-progress). A status of 409 indicates the
      // replace is either in preparing or failed state. We
      // can inspect the error detail for 'failed' to indicate a
      // replace error otherwise assume prepare is still in progress.
      //
      // If we do not get a 202 status within 30 seconds we should
      // just assume that the replace has failed.
      whDesignSnapshot = await warehouse.swapProduct({
        designId: replacement.designId(),
        hotspotInstanceUids: replacement.hotspotUuids(),
        oldProductId: replacement.replaceProductId(),
        newProductId: replacement.swapInProductId(),
        snapshotId: replacement.revisionUuid()
      });
      console.log('Designsnapshot', whDesignSnapshot); // intentionally leaving in

      if (whDesignSnapshot.status === 'failed') {
        throw new Error(`Live swap prepare failed for designsnapshot: ${whDesignSnapshot.id}`);
      }

      // Check for and handle cancelations.
      const swapHasBeenCanceled = () => {
        return currentReplacement().hasBeenCanceled();
      };

      const handleCanceledSwap = () => {
        dispatch(SwapService._handleCancelSwap({ incomingSwap, whDesignSnapshot }));

        // Display all hotspots
        if (updatedMessengerSwapUIEnabled) {
          dispatch(setMobileHotspotsView(MOBILE_HOTSPOTS_VIEWS.ALL_HOTSPOTS));
        }

        // Display cancelled notification
        dispatch(updateShowSwapNotification({
          status: true,
          design,
          productIds: [replacement.swapInProductId()],
          messageType: CURRENT_DESIGN_CANCELLED
        }));
      };

      if (swapHasBeenCanceled()) { return handleCanceledSwap(); }

      if (updatedMessengerSwapUIEnabled) {
        // Hide all hotspots outside of swapping hotspot
        dispatch(setMobileHotspotsView(MOBILE_HOTSPOTS_VIEWS.SWAPPING_HOTSPOTS_ONLY));
      }

      // Request the full design with swap/revison from BE.
      // This is so internal revision and render objects we
      // create will come from the exact same API call regardless
      // of whether we are doing a live swap or first load of a
      // design with revisions. Also, if the swap was initated from
      // a Modsy design, then this will return the new Playground
      // design.
      //
      // Note: for swaps initated from a Modsy design, we should NOT
      // show the modal allowing users to switch over to the Playground
      // until this API response returns. Granted, if Product requirements
      // are inflexible and require the modal and switch over are presented
      // at a fixed time, then we can always create a cloned fake design and
      // just overwrite it with the real one returned in this response.
      const updatedDesignBrief = Design.createDesignFromWarehouseResponse({
        spaceId: design.parentSpaceId(),
        whDesign: await warehouse.getDesignBrief(whDesignSnapshot.design_pk),
        showroomSpace: currentSpace().isShowroomSpace()
      });
      const updatedDesign = await warehouse.getDesign(whDesignSnapshot.design_pk);
      console.log('Design With Snapshot', updatedDesign); // intentionally leaving in

      const designsnapshot = updatedDesign.designsnapshot;
      if (designsnapshot.status === 'failed') {
        throw new Error(`Live swap prepare failed for designsnapshot: ${designsnapshot.id}`);
      }

      if (swapHasBeenCanceled()) { return handleCanceledSwap(); }

      // Update state with the design, revision, and renders.
      //
      // For swaps initiated from a Modsy design, we will be adding
      // the new playground design to state in addition to revision and
      // renders from the swap. We are assuming user will then switch over
      // to the new playground design. If the user cancels, then we need to
      // remove the new playground design from state.
      //
      // For swaps initiated from a playground design, we need to add the
      // revision and renders from the swap plus update the existing
      // playground design with the new revision uuid and swappedInProduct.
      const isNewDesign = updatedDesign.id !== design.id();
      let resultingDesign;
      if (isNewDesign) {
        dispatch(analytics.trackEvent({
          event: 'New Playground Design Created',
          data: {
            design_id: updatedDesign.id,
            space_id: design.parentSpaceId()
          }
        }));

        dispatch(studioDesignLoaded({
          spaceId: design.parentSpaceId(),
          whDesign: updatedDesign,
          showroomSpace: currentSpace().isShowroomSpace()
        }));

        resultingDesign = getDesignFor({
          designId: updatedDesign.id,
          state: getState()
        });

        // set author info from the new brief
        resultingDesign.setAuthorInfo(updatedDesignBrief.createdAt(), updatedDesignBrief.createdBy());

        // make copy with revision uuid
        const resultingDesignCopy = resultingDesign.makeCopyWithRevision({
          revisionUuid: designsnapshot.id,
          addlProducts: [swapInProduct.id()],
          removeProducts: [replaceProduct.id()]
        });

        // Update the space with new design.
        const updatedSpace = currentSpace().makeCopyWithNewDesign(resultingDesignCopy);
        dispatch(addOrUpdateSpace(updatedSpace));

        // Update the replacement with the new design ID and revision UUID.
        dispatch(SwapService._updateReplacementState(
          currentReplacement().makeCopy({
            designId: updatedDesign.id,
            revisionUuid: designsnapshot.id,
            isNewDesign: true
          })
        ));

        // Add copied design to redux
        dispatch(studioAddOrUpdateDesign(resultingDesignCopy));
      } else {
        dispatch(studioDesignsnapshotLoaded({ whDesignSnapshot: designsnapshot }));

        // Update the replacement with the new revision UUID.
        dispatch(SwapService._updateReplacementState(
          currentReplacement().makeCopy({
            revisionUuid: designsnapshot.id,
            isNewDesign: false
          })
        ));

        // Update playground design with the new revision and products.
        resultingDesign = design.makeCopyWithRevision({
          revisionUuid: designsnapshot.id,
          addlProducts: [swapInProduct.id()],
          removeProducts: [replaceProduct.id()]
        });
        dispatch(studioAddOrUpdateDesign(resultingDesign));
      }

      // Fetch render camera ids from updated design
      const resultingDesignRenders = resultingDesign.renders() || [];
      const renderCameraIds = resultingDesignRenders.map((render) => get(render, 'camera_id', ''));

      // Prepare array of RendersInProgress to fetch
      rendersToFetch = this._prepareRendersToFetch({
        renderCameraIds,
        whDesignSnapshot,
        hotspotUuids,
        swapInProduct
      });

      // Determine the render uuid in the revision that corresponds to
      // the render swap was initiated from.
      const selectedRender = rendersToFetch.find((render) => render.cameraId() === cameraId);
      const selectedRenderUuid = selectedRender && selectedRender.renderUuid();
      if (!selectedRenderUuid) {
        throw new Error(`Cannot find camera ${cameraId} in designsnapshot ${whDesignSnapshot.id}`);
      }

      const redirectToNewDesign = () => {
        if (updatedMessengerSwapUIEnabled) {
          let render = resultingDesign.findRenderByCamera(cameraId);

          if (!render) { render = resultingDesign.firstRender(); }

          const renderId = render.id;

          dispatch(studioSelectRender({ renderId }));

          navigateToStudioSpaceAndDesignFor({
            spaceId: design.parentSpaceId(),
            designId: updatedDesign.id
          });
        }
      };

      const renders = await dispatch(this.fetchRendersBatch({
        swapInCameraId: cameraId,
        swapInProduct,
        rendersToFetch,
        resultingDesign,
        whDesignSnapshot,
        swapHasBeenCanceled,
        handleCanceledSwap,
        currentSpace,
        currentReplacement,
        redirectToNewDesign
      }));

      if (swapHasBeenCanceled()) {
        return handleCanceledSwap(whDesignSnapshot);
      }

      if (!renders) {
        throw new Error('Error fetching new renders');
      }

      // Update design brief updateAt time.
      const updatedSpace = currentSpace().makeCopyUpdateDesignBriefUpdatedAt(
        updatedDesign.id, designsnapshot.updated_at
      );
      dispatch(addOrUpdateSpace(updatedSpace));

      // Show the swap success notification
      dispatch(updateShowSwapNotification({
        cameraId,
        design: resultingDesign,
        status: true,
        renderUid: selectedRender && selectedRender.renderUid(),
        productIds: [swapInProduct.id()],
        messageType: CURRENT_DESIGN_COMPLETE
      }));

      // the replacement phase has succeeded.
      // We can set the status to completed.
      const replacementComplete = currentReplacement().makeCopy({ status: 'Completed' });
      dispatch(SwapService._updateReplacementState(replacementComplete));

      // If we are on the same design as the swap and the currently
      // selected product is the replaced product, then select the
      // swapped in product.
      dispatch(this._selectSwappedProduct({
        renders,
        selectedRenderUuid,
        swapInProduct,
        currentReplacement,
        isFromHotspot
      }));

      dispatch(analytics.trackEvent({
        event: 'Studio: Swap Success',
        data: {
          design_id: design.id(),
          space_id: design.parentSpaceId(),
          replaced_product_id: replaceProduct.id(),
          swapIn_product_id: swapInProduct.id()
        }
      }));

      if (updatedMessengerSwapUIEnabled) {
        // Hide all hotspots outside of completed hotspot
        dispatch(setMobileHotspotsView(MOBILE_HOTSPOTS_VIEWS.SWAPPING_HOTSPOTS_ONLY));
      }
    } catch (error) {
      dispatch(SwapService._handleCancelSwap({ incomingSwap, whDesignSnapshot }));

      const rendersToCancel = rendersToFetch;
      if (rendersToCancel && rendersToCancel.length > 0) {
        const renderUids = rendersToCancel.map((render) => (render && render.renderUid()));
        dispatch(SwapService.cancelRenders(renderUids));
      }

      if (get(error, 'message') === 'Invalid hotspot UUID') {
        dispatch(updateShowSwapNotification({
          status: true,
          design,
          productIds: [replacement.swapInProductId()],
          messageType: PRODUCT_NOT_SWAPPABLE
        }));
      } else if (error.toString().includes('Network Error')) {
        dispatch(updateShowSwapNotification({
          status: true,
          design,
          productIds: [replacement.swapInProductId()],
          messageType: NETWORK_ERROR
        }));
      } else {
        dispatch(updateShowSwapNotification({
          status: true,
          design,
          productIds: [replacement.swapInProductId()],
          messageType: CURRENT_DESIGN_FAILED
        }));
      }

      if (get(error, 'message').includes('fetchRender failed')) {
        // update render variant in revision as 'failed'
        // otherwise they stay as unfinished even though we got an error

        // extract renderUuid
        let message = get(error, 'message');
        let renderUuids = message.split(' ').pop().split(',');
        SwapService.updateRendersFailed(renderUuids, dispatch, getState());
      }

      dispatch(SwapService._handleSwapError(
        SwapService.SWAP_ERROR_PRODUCT_NOT_SWAPPABLE,
        undefined,
        incomingSwap,
        error,
        design
      ));
    }
  };

  /** @private */
  static _selectSwappedProduct = ({
    renders,
    selectedRenderUuid,
    swapInProduct,
    isFromHotspot = false,
    currentReplacement = () => {}
  } = {}) => (dispatch, getState) => {
    const activeDesign = getActiveDesign({ state: getState() });
    const selectedProduct = getSelectedHotspotProduct(getState());

    if (
      activeDesign &&
      activeDesign.id() === currentReplacement().designId() &&
      selectedProduct &&
      selectedProduct.id() === currentReplacement().replaceProductId()
    ) {
    // Find the swapInProduct in the new render and select it.
    // If we cannot find a hotspot matching the product in the
    // new render, then fallback to unselecting the hotspot.
      const selectedRender = renders.find((r) => r.id === selectedRenderUuid);
      const newHotspot = selectedRender.hotspots.find(hotspot => hotspot.product_id === swapInProduct.id());
      const hotspotUid = newHotspot ? newHotspot.instance_id : null;
      const productId = hotspotUid ? swapInProduct.id() : null;
      dispatch(selectHotspotProduct(productId, hotspotUid, isFromHotspot));
    }
  };

  /** @private */
  static _prepareRendersToFetch = ({
    renderCameraIds,
    whDesignSnapshot,
    hotspotUuids,
    swapInProduct
  } = {}) => {
    const snapshotRenderingsData = get(whDesignSnapshot, 'renderings', {});

    // Filter out render camera ids which are not present in updated snapshot
    const filteredRenderCameraIds = renderCameraIds.filter((cameraId) => {
      return snapshotRenderingsData.hasOwnProperty(cameraId);
    });

    return filteredRenderCameraIds.map((cameraId) => {
      const render = get(snapshotRenderingsData, cameraId, {});
      const revisionUuid = get(whDesignSnapshot, 'id', '');
      const renderUid = Render.createRenderUid({ revisionUuid, cameraName: cameraId });
      const renderUuid = get(render, 'live_swap', '');
      const productIds = [swapInProduct && swapInProduct.id()];

      return new RenderInProgress({
        renderUid,
        renderUuid,
        cameraId,
        revisionUuid,
        productIds,
        hotspotUuids,
        status: 'InProgress'
      });
    });
  };

  /** @private */
  static _handleSwapError = (swapError, message, metaData, error, design) => (dispatch, getState) => {
    const replacement = getSwapData(getState()).makeCopy({ status: 'Error', error: swapError });
    dispatch(SwapService._updateReplacementState(replacement));
    bugsnagError(error || message, metaData);

    const {
      replaceProduct,
      swapInProduct
    } = metaData;

    dispatch(analytics.trackEvent({
      event: 'Studio: Swap Error',
      data: {
        design_id: design.id(),
        error: get(error, 'message', ''),
        space_id: design.parentSpaceId(),
        replaced_product_id: replaceProduct.id(),
        swapIn_product_id: swapInProduct.id()
      }
    }));

    console.log(message, error); // intentionally leaving in
  };

  /** @private */
  static _updateReplacementState = (replacement) => dispatch => {
    dispatch({
      type: SwapService.REPLACEMENT_DATA_UPDATED,
      payload: replacement
    });
  };

  /** @private */
  static _handleCancelSwap = ({
    incomingSwap = {},
    whDesignSnapshot = null
  } = {}) => async (dispatch, getState) => {
    const { design } = incomingSwap;

    // Design id and space id from design where
    // swap was initiated
    const designId = design && design.id();
    const spaceId = design && design.parentSpaceId();

    dispatch(analytics.trackEvent({
      event: 'Studio: Swap Cancel',
      data: {
        design_id: designId,
        space_id: spaceId
      }
    }));

    // If the cancel occurred before the revision/designsnapshot was
    // created or there was an error creating the designsnapshot, then
    // there is nothing to do.
    if (!whDesignSnapshot) {
      return;
    }

    const revisionDesignId = whDesignSnapshot.design_pk;
    const revisionDesign = getDesignFor({
      designId: revisionDesignId,
      state: getState()
    });

    // If the design+revision was created but never added to state
    // then we need to abandon the design.
    if (!revisionDesign) {
      try {
        warehouse.abandonDesign(revisionDesignId);
      } catch (error) {
        bugsnagError(error, { revisionDesignId });
      }
      return;
    }

    // If the incoming design is the same as the snapshot design
    // then the swap was initiated from a playground design and
    // we should hit the cancel revision BE API.
    //
    // Otherwise the swap was initiated from a Modsy design and
    // we should take one of two paths:
    //  1. if the active design matches the revision design then
    //     we should hit the cancel revision BE API
    //  2. otherwise we should abandon the playground design

    // Only check if incoming swap was initiated from a modsy design for studio
    const isPlaygroundDesign = design && design.isPlayground();

    if (isPlaygroundDesign) {
      // Update the session revision history and pointer
      // if the design has already been updated.
      if (revisionDesign.revisionUuid() === whDesignSnapshot.id) {
        dispatch(studioAddOrUpdateDesign(revisionDesign.makeCopyCancelRevision()));
      }
      // Update the server.
      try {
        const response = await warehouse.cancelRevision(revisionDesignId);
        console.log(`canceled revision ${whDesignSnapshot.id} from ${revisionDesignId}:`, response); // intentionally leaving in
      } catch (error) {
        bugsnagError(error, { revisionDesign });
      }
    } else {
      // Remove the revision design from this session.
      console.log(`abandoned ${revisionDesignId}`); // intentionally leaving in
      dispatch(studioDeleteDesign(revisionDesign));

      // Navigate back to design where swap
      // was initiated
      navigateToStudioSpaceAndDesignFor({
        spaceId,
        designId
      });
    }
  };

  /**
   * Cancels an in-progress swap.
   * Currently just sets the status flag on the replacement to canceled.
   */
  static cancelSwap = () => (dispatch, getState) => {
    const replacement = getSwapData(getState());
    dispatch(SwapService._updateReplacementState(
      replacement.makeCopy({ status: 'Canceled' })
    ));
  };

  /**
   * Batch entry point for initiating multiple renders in progress.
   * These are all kicked off in batch and polled until a render job errors,
   * all render jobs complete, or LiveSwap.MAX_LIVESWAP_ATTEMPTS elapses without
   * resolution
   * param {Object} renderDetails
   * @param {string} renderDetails.swapInCameraId - The camera name for render swap was initiated from
   * @param {Product} renderDetails.swapInProduct - The Product instance to swap in
   * @param {RenderInProgress[]} renderDetails.rendersToFetch - List of RendersInProgress to fetch as batch
   * @param {Design} renderDetails.resultingDesign - Updated Design instance with latest revision
   * @param {Object} renderDetails.whDesignSnapshot - Warehouse response data for new swap post
   * @param {Object} renderDetails.updatedDesign - Warehouse response data for updated design
   * @param {function} renderDetails.swapHasBeenCanceled - Function to check if swap has been canceled
   * @param {function} renderDetails.handleCanceledSwap - Function to handle canceled swap
   * @param {function} renderDetails.currentSpace - Function to fetch current space
   * @param {function} renderDetails.currentReplacement - Function to check current design replacement
   */
  static fetchRendersBatch = ({
    swapInCameraId = '',
    swapInProduct = {},
    rendersToFetch = [],
    resultingDesign = {},
    whDesignSnapshot = null,
    updatedDesign = {},
    swapHasBeenCanceled = () => {},
    handleCanceledSwap = () => {},
    currentSpace = () => {},
    currentReplacement = () => {},
    redirectToNewDesign = () => {}
  } = {}) => async (dispatch, getState) => {
    // Poll on the revision render for camera swap was initiated on.
    // When we get a finished status we know that the replacement
    // was successful. Here, we will do up to 24 polls at an inteveral
    // of 5 seconds before timing out and assuming the swap failed.
    let renders = null;
    let updatedDesignSnapshot = false;

    // Filter out renders already in progress and add new render jobs to state
    const filteredRendersToFetch = rendersToFetch.filter((render) => {
      const renderUid = render && render.renderUid();
      const renderUuid = render && render.renderUuid();
      const cameraId = render && render.cameraId();

      // check that required attributes are present
      const isValidRenderInProgress = renderUid && renderUuid && cameraId;

      // check that render in progress does not already exist
      const existing = dispatch(getCurrentRenderInProgress({ renderUid }));
      const isExistingRenderInProgress = existing && existing.renderUuid() && (
        existing.isInProgress() || existing.isComplete() || existing.hasFailed()
      );
      return isValidRenderInProgress && !isExistingRenderInProgress;
    });

    dispatch(SwapService._updateRendersInProgressState(filteredRendersToFetch));

    const renderingUuids = filteredRendersToFetch.map((render) => render.renderUuid());

    for (let attempt = 1; attempt <= SwapService.MAX_LIVESWAP_ATTEMPTS; attempt++) {
      if (swapHasBeenCanceled()) { return handleCanceledSwap(whDesignSnapshot); }
      const data = await warehouse.batchGetRenders({ renderingUuids, attempt });
      renders = data.responses.map((response) => response.rendering);
      console.log(`Fetch Swap Renders ${renderingUuids.join(',')} - Attempt ${attempt}`, renders); // intentionally leaving in

      // when all renders in batch progress past the preparing phase for the first time
      // get the design snapshot again
      // this updates the newInstanceIds for revisions
      if (renders.every(render => (render.status !== 'preparing' && render.status !== 'failed')) && !updatedDesignSnapshot) {
        updatedDesignSnapshot = true;
        let designSnapshot = (await warehouse.getDesign(whDesignSnapshot.design_pk)).designsnapshot;
        if (designSnapshot) { dispatch(studioDesignsnapshotLoaded({ whDesignSnapshot: designSnapshot })); }
      }

      if (renders.every(render => (render.status === 'finished'))) {
        break;
      }

      // if any renders fail cancel swap
      if (renders.some(render => (render.status === 'failed'))) {
        const failedRenders = renders.filter(render => (render.status === 'failed'));
        const failedUuids = failedRenders.map(render => render.id);
        throw new Error('fetchRender failed: ' + failedUuids.join(','));
      }

      if (swapHasBeenCanceled()) { return handleCanceledSwap(whDesignSnapshot); }

      // redirect to new design after first attempt
      if (attempt === 1) { redirectToNewDesign(); }

      if (attempt === SwapService.MAX_LIVESWAP_ATTEMPTS / 2) {
        // Show the 'taking longer' notification
        dispatch(updateShowSwapNotification({
          cameraId: swapInCameraId,
          design: resultingDesign,
          status: true,
          productIds: [swapInProduct.id()],
          messageType: LONGER_SWAP
        }));
      }

      await wait5seconds();
    }

    // refresh current renders in progress from redux
    const updatedRendersInProgress = filteredRendersToFetch.map((renderInProgress) => {
      const renderUid = renderInProgress && renderInProgress.renderUid();
      const renderUuid = renderInProgress && renderInProgress.renderUuid();
      const render = renders.find((render) => render.id === renderUuid);

      const existing = dispatch(getCurrentRenderInProgress({ renderUid }));
      return existing && existing.makeCopy({
        status: render.status === 'finished' ? 'Complete' : 'Error',
        error: 'fetch render failed'
      });
    });

    dispatch(SwapService._updateRendersInProgressState(updatedRendersInProgress));

    if (swapHasBeenCanceled()) { return handleCanceledSwap(whDesignSnapshot); }

    if (renders.some(render => (render.status === 'failed'))) {
      const failedRenders = renders.filter(render => (render.status === 'failed'));
      const failedUuids = failedRenders.map(render => render.id);
      throw new Error('fetchRender failed: ' + failedUuids.join(','));
    }

    // throw special error for prepare timing out
    if (renders.some(render => (render.status === 'preparing'))) {
      throw new Error(`Live swap prepare timed out for designsnapshot: ${whDesignSnapshot.id}`);
    }

    if (renders.some(render => (render.status !== 'finished'))) {
      throw new Error('Live swap render timed out');
    }

    // ========
    // SUCCESS
    // ========
    // Update Render instances in state to trigger displaying the new renders.
    const updatedRenders = rendersToFetch.map(render => {
      const revisionUuid = render && render.revisionUuid();
      const renderUuid = render && render.renderUuid();
      const cameraId = render && render.cameraId();

      const renderUid = Render.createRenderUid({
        revisionUuid,
        cameraName: cameraId
      });
      const renderV2 = getRenderFor({ renderUid, state: getState() });
      const updatedDraftVariant = renders.find((r) => r.id === renderUuid);
      return updatedDraftVariant && renderV2 && renderV2.makeCopyWithUpdatedDraftVariant(updatedDraftVariant);
    });

    dispatch(studioAddOrUpdateRenders(updatedRenders));

    return renders;
  };

  /**
   * Main entry point for initiating a render in progress.
   * There can be many renders in progress at one time.
   * These are all specifically DRAFT renders. We do not
   * poll on beauty renders.
   * param {Object} renderDetails
   * @param {string} renderDetails.cameraId - The camera name
   * @param {string[]} renderDetails.hotspotUuids - Hotspots that should get spinners while fetching.
   * @param {string[]} renderDetails.productIds - Products that should get spinners while fetching.
   * @param {string} renderDetails.revisionUuid - The parent revision UUID
   * @param {string} renderDetails.renderUuid - The render UUID
   * @fires SwapService#ripUpdated
   */
  static fetchRender = ({
    cameraId,
    design,
    hotspotUuids,
    productIds,
    revisionUuid,
    renderUuid,
    variantType
  }) => async (dispatch, getState) => {
    const renderUid = Render.createRenderUid({ revisionUuid, cameraName: cameraId });
    const getRip = () => getRenderInProgress({ renderUid, state: getState() });

    const fetchHasBeenCanceled = () => {
      return getRip().hasBeenCanceled();
    };

    // We will wrap the entire action in a try/catch
    // with a catchall error status of fetch failed
    // for any errors not explicitly handled.
    try {
      // Handle null render instance.
      if (!revisionUuid || !renderUuid || !cameraId) {
        return bugsnagError('Bad fetchRender inputs', { cameraId, revisionUuid, renderUuid });
      }

      // Handle existing RenderInProgress for the same ID.
      // If the status is InProgress OR Complete, then return (i.e. de-dup).
      //
      // If the status is Error, then return. This prevents an infinite
      // request loop when the rendering is failing. To retry a failed
      // render we need some UI event and in the handler set the status
      // back to NotStarted or Canceled before requesting the render again.
      //
      // For all other status, start a new fetch.
      const existing = getRip();
      if (existing &&
        // TODO: Add condition to check if the render in progress is of the
        // same type (i.e. draft, beauty) using the renderUuid
        // since getActiveRender fires all the time, it blocks the beauty
        // render from being fetched
        // need a better solution here
        existing.renderUuid() === renderUuid && (
          existing.isInProgress() ||
          existing.isComplete() ||
          existing.hasFailed()
        )
      ) {
        return;
      }

      // Create RenderInProgress instance.
      const renderInProgress = new RenderInProgress({
        cameraId,
        hotspotUuids,
        productIds,
        renderUid,
        renderUuid,
        revisionUuid,
        status: 'InProgress'
      });

      // Update state with new instance
      dispatch(SwapService._updateRenderInProgressState(renderInProgress));

      // Poll on the revision render for camera swap was initiated on.
      // When we get a finished status we know that the replacement
      // was successful. Here, we will do up to 48 polls at an inteveral
      // of 5 seconds before timing out and assuming the render failed.
      let render = null;
      for (let attempt = 1; attempt <= 96; attempt++) {
        render = await warehouse.getRender({ renderUuid, attempt });
        console.log(`Fetch Render ${renderUuid} - Attempt ${attempt}`, render); // intentionally leaving in

        if (render.status === 'finished') {
          break;
        }

        if (render.status === 'failed') {
          throw new Error('fetchRender failed: ' + renderUid);
        }

        if (fetchHasBeenCanceled()) { return; }
        await wait5seconds();
      }

      if (fetchHasBeenCanceled()) { return; }

      if (render.status !== 'finished') {
        throw new Error('fetchRender timed out');
      }

      const updatedRip = getRip().makeCopy({ status: 'Complete', error: null });
      dispatch(SwapService._updateRenderInProgressState(updatedRip));

      // ========
      // SUCCESS!
      // ========
      // Update Render instance in state to trigger displaying the new render.
      const renderV2 = getRenderFor({ renderUid, state: getState() });
      let updatedRenderV2;

      if (variantType === Render.TYPE_BEAUTY) {
        updatedRenderV2 = renderV2.makeCopyWithUpdatedBeautyVariant(render);
      } else {
        updatedRenderV2 = renderV2.makeCopyWithUpdatedDraftVariant(render);
      }

      dispatch(studioAddOrUpdateRenders([updatedRenderV2]));
    } catch (error) {
      const rip = getRip();
      if (rip) {
        const updatedRip = rip.makeCopy({ status: 'Error', error: error.message });
        dispatch(SwapService._updateRenderInProgressState(updatedRip));
      }

      if (get(error, 'message').includes('fetchRender failed')) {
        // update render variant in revision as 'failed'
        // otherwise they stay as unfinished even though we got an error

        // extract renderUuid
        let renderUuid = get(error, 'message').split(' ').pop();
        SwapService.updateRenderFailed(renderUuid, dispatch, getState());
      }

      bugsnagError(error, { renderUuid });
      console.log(error); // intentionally leaving in
    }
  };

  /** Cancels multiple renders in progress. */
  static cancelRenders = (renderUids) => (dispatch, getState) => {
    const rendersInProgress = getRendersInProgress(getState());

    // Filter out renders not in state
    const filteredRenderUids = renderUids.filter((renderUid) => {
      const renderUidExists = !!rendersInProgress[renderUid];
      if (!renderUidExists) {
        bugsnagWarn('Attempted to cancel not existing render in progress', { renderUid });
      }
      return renderUidExists;
    });

    // Fetch updated copies and update state
    const updatedRendersInProgress = filteredRenderUids.map((renderUid) => {
      return rendersInProgress[renderUid].makeCopy({ status: 'Canceled' });
    });
    dispatch(SwapService._updateRendersInProgressState(
      updatedRendersInProgress
    ));
  };

  /** Cancels a render in progress. */
  static cancelRender = (renderUid) => (dispatch, getState) => {
    const rip = getRenderInProgress({ renderUid, state: getState() });
    if (!rip) {
      bugsnagWarn('Attempted to cancel not existing render in progress', { renderUid });
      return;
    }

    dispatch(SwapService._updateRenderInProgressState(
      rip.makeCopy({ status: 'Canceled' })
    ));
  };

  /**
   * Used to fetch all draft renders for a designs latest revision.
   * This was made primarily for use after saving a design from
   * Bessie and then viewing your new design in the Studio
   * This should only be passed designs with a current revision
   * param {Design} The instance of Design whose latest renders you want initiated and fetched.
   */
  static fetchLatestRevisionRenders = (revision, variantType) => async (dispatch, getState) => {
    const design = getDesignFor({ designId: revision.parentDesignId(), state: getState() });
    const renders = revision.renders();
    let renderUid;

    Object.keys(renders).forEach(cameraName => {
      try {
        const renderUid = renders[cameraName];
        const render = getRenderFor({ renderUid, state: getState() });

        // TODO: skipping 360 renders for showroom
        // prob need a better way for this
        if (design.isShowroomDesign()) {
          const designRenderData = design.findRenderByCamera(render.cameraName());

          if (designRenderData.camera_id !== render.cameraName()) { return; }
        }

        const variantComplete = render.variantFinishedOrFailed(variantType);

        // if no render or the variant has finished or failed
        // don't fetch the variant
        if (!render || variantComplete) { return; }

        const variant = variantType === Render.TYPE_BEAUTY ? render.beautyVariant() : render.draftVariant();
        const renderUuid = variant.uuid();

        dispatch(SwapService.fetchRender({
          cameraId: cameraName,
          design,
          productIds: [],
          renderUuid,
          revisionUuid: design.revisionUuid(),
          variantType
        }));
      } catch (error) {
        bugsnagError(error, {
          designId: design.id(),
          revisionUuid: design.revisionUuid(),
          renderUid
        });
      }
    });
  };

  /** @private */
  static _updateRendersInProgressState = (rendersInProgress) => dispatch => {
    dispatch({
      type: SwapService.RENDERS_IN_PROGRESS_UPDATED,
      payload: rendersInProgress
    });
  };

  /** @private */
  static _updateRenderInProgressState = (rip) => dispatch => {
    dispatch({
      type: SwapService.RENDER_IN_PROGRESS_UPDATED,
      payload: rip
    });
  };

  /**
   * Handles the logic for swap "Undo".
   * This does not delete any revision, it simply
   * moves the current revision pointer one back
   * in the revision history for the input design.
   * @param {Design} design - The design to appy the revision undo action to.
   */
  static undoRevision = (design) => (dispatch, getState) => {
    const revisionUuid = design.revisionUuid();
    if (!revisionUuid) {
      bugsnagError(
        'Undo attempted for design with no current revision',
        { designId: design.id() }
      );
      return;
    }

    // Here, we will assume that the server request is
    // going to succeed and not hold up the user.
    //
    // Update the current revision pointer for this design.
    const updatedDesign = design.makeCopyUndoRevision();
    dispatch(studioAddOrUpdateDesign(updatedDesign));
  };

  /**
   * Handles the logic for swap "Redo".
   * This does not delete any revision, it simply
   * moves the current revision pointer one forward
   * in the revision history for the input design.
   * @param {Design} design - The design to appy the revision redo action to.
   */
  static redoRevision = (design) => (dispatch, getState) => {
    if (!design.hasRevisions()) {
      bugsnagError(
        'Redo attempted for design with no revisions',
        { designId: design.id() }
      );
      return;
    }

    // Here, we will assume that the server request is
    // going to succeed and not hold up the user.
    //
    // Update the current revision pointer for this design.
    const updatedDesign = design.makeCopyRedoRevision();
    dispatch(studioAddOrUpdateDesign(updatedDesign));
  };

  // ===================
  // SELECTORS / GETTERS
  // ===================
  static defaultEmptyArray = [];

  static replacementIsInProgressFor = ({ designId, revisionUuid, cameraId, state }) => {
    const replacement = getSwapData(state);
    return (
      (replacement && replacement.isInProgress()) &&
      (designId || revisionUuid || cameraId) &&
      (!designId || replacement.designId() === designId) &&
      (!revisionUuid || replacement.revisionUuid() === revisionUuid) &&
      (!cameraId || replacement.cameraId() === cameraId)
    );
  };

  static replacementDesignId = ({ state }) => {
    const replacement = getSwapData(state);
    return (replacement ? replacement.designId() : null);
  };

  static replacementIsInProgress = ({ state }) => {
    const replacement = getSwapData(state);
    return (replacement && replacement.isInProgress());
  };

  static replacementIsNewDesign = ({ state }) => {
    const replacement = getSwapData(state);
    return (replacement && replacement.isNewDesign());
  };

  static getReplacingProductId = (state) => {
    const replacement = SwapService._getReplacementMatchingActiveDesign(state);
    return (replacement && replacement.isInProgress())
      ? replacement.swapInProductId()
      : 0;
  };

  static getReplacedProductId = (state) => {
    const replacement = SwapService._getReplacementMatchingActiveDesign(state);
    return (replacement && replacement.isInProgress())
      ? replacement.replaceProductId()
      : 0;
  }

  static getFetchingProductIds = (state) => {
    const rip = SwapService._getActiveRenderInProgress(state);
    return (rip && rip.isInProgress()) ? rip.productIds() : SwapService.defaultEmptyArray;
  };

  static getReplacingHotspotUuids = (state) => {
    const replacement = SwapService._getReplacementMatchingActiveDesign(state);
    return (replacement && replacement.isInProgress())
      ? replacement.hotspotUuids()
      : null;
  };

  static getFetchingHotspotUuids = (state) => {
    const rip = SwapService._getActiveRenderInProgress(state);
    return (rip && rip.isInProgress()) ? rip.hotspotUuids() : SwapService.defaultEmptyArray;
  };

  static getCurrentSwappingProducts = createSelector(
    (state) => SwapService.getFetchingProductIds(state),
    (state) => SwapService.getReplacingProductId(state),
    (state) => getIndexedProducts(state),
    (fetchingProductIds, replacingProductId, allProducts) => {
      const swapProductIds = unique([
        ...fetchingProductIds,
        replacingProductId
      ].filter(id => {
        return !!id;
      }));

      const swapProducts = swapProductIds
        .map(productId => allProducts[productId])
        .filter(product => !!product);

      return swapProducts;
    }
  );

  /** @private */
  static _getReplacementMatchingActiveDesign = (state) => {
    const design = getActiveDesign({ state });
    const replacement = getSwapData(state);
    return (
      !design ||
      !replacement ||
      replacement.designId() !== design.id()
    ) ? null : replacement;
  };

  /** @private */
  static _getActiveRenderInProgress = (state) => {
    const design = getActiveDesign({ state });
    const render = getActiveRender(state);
    if (!design || !render) { return null; }

    const renderUid = Render.createRenderUid({
      revisionUuid: design.revisionUuid(),
      cameraName: render.camera_id
    });

    return getRenderInProgress({ renderUid, state });
  };

  /**
   * Update a render's (and its variant's) status to 'failed'
   */
  static updateRenderFailed = (renderUuid, dispatch, state) => {
    let renders = getRenders(state);

    if (renders[renderUuid]) {
      renders[renderUuid] = renders[renderUuid].setFailed();
      dispatch(studioAddOrUpdateRenders(Object.values(renders)));
    }
  };

  /**
   * Update multiple renders' (and their variants') statuses to 'failed'
   */
  static updateRendersFailed = (renderUuids, dispatch, state) => {
    const renders = getRenders(state);

    // Update given renders to status failed
    // and filter renders not in state
    const updatedRenders = renderUuids.map((renderUuid) => {
      const failedRender = renders[renderUuid];
      return failedRender && failedRender.setFailed();
    }).filter((updatedRender) => !!updatedRender);

    dispatch(studioAddOrUpdateRenders(updatedRenders));
  };
}

// =================
// UTILITY FUNCTIONS
// =================
const wait5seconds = () => waitMilliseconds(5000);

const waitMilliseconds = (milliseconds) => new Promise(resolve => {
  setTimeout(() => { resolve(); }, milliseconds);
});
